import type { ShiftStats } from './MissionManager';

export interface ShiftSummaryHandlers {
  onStartNew: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export class ShiftSummary {
  private overlay: HTMLElement;

  constructor(private handlers: ShiftSummaryHandlers) {
    this.injectStyles();
    this.overlay = el('div');
    this.overlay.id = 'shift-summary';
    this.overlay.style.display = 'none';
    document.body.appendChild(this.overlay);
  }

  show(stats: ShiftStats) {
    while (this.overlay.firstChild) this.overlay.removeChild(this.overlay.firstChild);

    const card = el('div', 'ss-card');
    card.appendChild(el('h1', 'ss-title', 'Shift Complete'));

    const rows = el('div', 'ss-rows');
    const addRow = (label: string, value: string, extraClass?: string) => {
      const row = el('div', 'ss-row');
      row.appendChild(el('span', undefined, label));
      const v = el('span', extraClass, value);
      row.appendChild(v);
      rows.appendChild(row);
    };

    const cleanPct = stats.rides ? Math.round((stats.cleanRides / stats.rides) * 100) : 0;
    addRow('Rides', String(stats.rides));
    addRow('Earnings', `$${stats.earnings.toFixed(2)}`, 'ss-gold');
    addRow('Best fare', `$${stats.bestFare.toFixed(2)}`);
    addRow('Clean rides', `${stats.cleanRides}/${stats.rides} (${cleanPct}%)`);
    addRow('Longest streak', String(stats.longestStreak));

    card.appendChild(rows);

    const btn = el('button', 'am-btn am-btn-primary ss-btn', 'Start New Shift');
    btn.addEventListener('click', () => this.handlers.onStartNew());
    card.appendChild(btn);

    this.overlay.appendChild(card);
    this.overlay.style.display = 'flex';
  }

  hide() {
    this.overlay.style.display = 'none';
    while (this.overlay.firstChild) this.overlay.removeChild(this.overlay.firstChild);
  }

  private injectStyles() {
    if (document.getElementById('ss-styles')) return;
    const style = document.createElement('style');
    style.id = 'ss-styles';
    style.textContent = `
      #shift-summary {
        position: fixed; inset: 0; z-index: 2000;
        display: flex; align-items: center; justify-content: center;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
      }
      .ss-card {
        font-family: var(--am-font);
        background: linear-gradient(180deg, var(--am-dark-2) 0%, var(--am-dark-1) 100%);
        color: #fff;
        padding: 32px 40px;
        border-radius: var(--am-radius-card);
        min-width: min(340px, 92vw);
        max-width: min(420px, 92vw);
        max-height: 92vh; overflow-y: auto;
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
      }
      @media (max-width: 520px), (max-height: 520px) {
        .ss-card { padding: 22px 24px; }
        .ss-title { font-size: 1.3rem; margin-bottom: 16px; }
        .ss-row { font-size: 0.82rem; }
        .ss-rows { gap: 9px; margin-bottom: 18px; }
      }
      .ss-title {
        margin: 0 0 22px;
        font-family: var(--am-font);
        font-size: 1.7rem; font-weight: 800;
        text-align: center; letter-spacing: 0.1em; text-transform: uppercase;
        background: linear-gradient(90deg, var(--am-gold), var(--am-gold-deep));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      }
      .ss-rows {
        display: flex; flex-direction: column; gap: 12px; margin-bottom: 26px;
      }
      .ss-row {
        display: flex; justify-content: space-between;
        font-size: 0.92rem; font-weight: 500;
      }
      .ss-row span:first-child { opacity: 0.65; }
      .ss-row span:last-child { font-weight: 700; font-variant-numeric: tabular-nums; }
      .ss-gold { color: var(--am-gold) !important; font-size: 1.15rem !important; }
      /* Full-width variant of the shared primary button */
      .ss-btn {
        width: 100%;
        padding-top: 12px; padding-bottom: 12px;
        font-size: 0.8rem;
      }
    `;
    document.head.appendChild(style);
  }
}
